import styled from '@emotion/styled';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import { Menu } from '../components/layout/menu';
import { UploadProgressBar } from '../components/upload-progress';
import {
  appAdminMenu,
  appMainMenu,
  appStudentMenu,
} from '../constants/app-main-menu';
import { LocaleKeys } from '../locale';
import { useAuthSelector } from '../state/auth/selectors';
import { useMeSelector } from '../state/me/selectors';
import { Paths } from '../types/common';

const MOBILE_CHROME_IDLE_MS = 3200;

const Wrapper = styled('main', {
  shouldForwardProp: (prop) => prop !== '$mobileChromeVisible',
})<{ $mobileChromeVisible: boolean }>`
  --app-mobile-menu-height: 68px;

  position: relative;
  padding-left: 140px;
  width: 100%;

  @media (max-width: 948px), (pointer: coarse) {
    padding-left: 0;
    padding-bottom: var(--app-mobile-menu-height);
  }
`;

const Content = styled.div`
  width: 100%;
  padding: 20px;

  @media (max-width: 738px) {
    padding: 12px;
  }
`;

const isRunningInsideFrame = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export const BaseLayout: FC = () => {
  const { t } = useTranslation([LocaleKeys.MENU]);
  const { meData } = useMeSelector();
  const { data: authData } = useAuthSelector();
  const location = useLocation();
  const hideChromeTimeoutRef = useRef<number | null>(null);
  const [mobileChromeVisible, setMobileChromeVisible] = useState(true);
  const embedded = new URLSearchParams(location.search).get('embedded') === '1';
  const framed = isRunningInsideFrame();
  const isStudentSession =
    meData?.role === 0 || !!authData?.account.studentMode;
  const mobileCourseDockReserved =
    location.pathname.startsWith(`${Paths.PLAYCOURSE}/`) &&
    !/(?:\/files\/\d+|\/items\/\d+)(?:\/)?$/.test(location.pathname);

  const isMobileChromeLayout = useCallback(() => {
    if (typeof window === 'undefined') return false;

    return (
      window.innerWidth <= 948 || window.matchMedia('(pointer: coarse)').matches
    );
  }, []);

  const clearChromeTimer = useCallback(() => {
    if (hideChromeTimeoutRef.current !== null) {
      window.clearTimeout(hideChromeTimeoutRef.current);
      hideChromeTimeoutRef.current = null;
    }
  }, []);

  const scheduleChromeHide = useCallback(() => {
    clearChromeTimer();
    if (!isMobileChromeLayout()) {
      setMobileChromeVisible(true);
      return;
    }

    hideChromeTimeoutRef.current = window.setTimeout(() => {
      setMobileChromeVisible(false);
    }, MOBILE_CHROME_IDLE_MS);
  }, [clearChromeTimer, isMobileChromeLayout]);

  const revealMobileChrome = useCallback(() => {
    if (!isMobileChromeLayout()) {
      setMobileChromeVisible(true);
      clearChromeTimer();
      return;
    }

    setMobileChromeVisible(true);
    scheduleChromeHide();
  }, [clearChromeTimer, isMobileChromeLayout, scheduleChromeHide]);

  const appMenu = useMemo(() => {
    if (isStudentSession) {
      return appStudentMenu(t);
    }
    const menu = appMainMenu(t);
    if (meData?.role === 3 || meData?.canAssignTeacherRole) {
      return [...menu, ...appAdminMenu(t)];
    }

    return menu;
  }, [isStudentSession, meData?.canAssignTeacherRole, meData?.role, t]);

  useEffect(() => {
    revealMobileChrome();

    return clearChromeTimer;
  }, [clearChromeTimer, location.pathname, revealMobileChrome]);

  useEffect(() => {
    const handleActivity = () => {
      revealMobileChrome();
    };
    const handleResize = () => {
      revealMobileChrome();
    };

    window.addEventListener('pointerdown', handleActivity, { passive: true });
    window.addEventListener('touchstart', handleActivity, { passive: true });
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('pointerdown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [revealMobileChrome]);

  if (embedded || framed) {
    return <Outlet />;
  }

  return (
    <Wrapper
      $mobileChromeVisible={mobileChromeVisible}
      data-mobile-chrome-visible={mobileChromeVisible ? 'true' : 'false'}
    >
      <Menu
        list={appMenu}
        mobileChromeVisible={mobileChromeVisible}
        mobileCourseDockReserved={mobileCourseDockReserved}
      />
      <Content>
        <Outlet />
      </Content>

      <UploadProgressBar />
    </Wrapper>
  );
};
